/* eslint-disable no-underscore-dangle */
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, NavigationStart, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ActionPage } from '../../class/action-page.class';
import { dependencies } from '../../class/workflow.dependency';
import { WorkflowNavItem } from '../../dynamic-render/shared/workflow-navigation/workflow-navigation.type';
import { getFlowInfo } from '../../helper/flow-utility/flow-utility';
import { WorkflowPageNavigationStateService } from '../workflow-page-navigation-state/workflow-page-navigation-state.service';
import { WorkflowService } from '../workflow/workflow.service';
import { LogicFnRef } from '@workflow/class/workflow.type';
import { StatefulService } from '@app/service/stateful.service';
import { PageInstance } from '@workflow/class/page.service';
import { FlowInstance } from '@workflow/class/flow.service';
import { WorkflowLoaderService } from '@shared/service/workflow-loader.service';
import { SawDust } from '@cudirect/sawdust/sawdust';
import { registerDevtoolsState } from '@shared/service/devtools/devtools-state-registry.service';

export enum NavigationEventType {
  NavigateNextPage = 'NavigateNextPage',
  NavigatePreviousPage = 'NavigatePreviousPage',
  NavigateToCurrentPage = 'NavigateToCurrentPage',
  NavigateToPage = 'NavigateToPage',
  // eslint-disable-next-line @typescript-eslint/no-shadow
  NavigationEnd = 'NavigationEnd'
}

export interface NavigationEvent {
  page: PageInstance;
  flow: FlowInstance;
  eventType: NavigationEventType;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
}

/**
 * A service to manage side navigation state and behavior
 */
@Injectable({
  providedIn: 'root'
})

// TODO: revisit / refactor
// rename this to Navigation service
export class WorkflowPageNavigationService {
  private readonly statefulService = inject(StatefulService);
  private readonly workflowLoader = inject(WorkflowLoaderService);
  private readonly logger = inject(SawDust);
  private logContext(additional: Record<string, unknown> = {}) {
    const currentPageId = this.workflowService?.currentPage?.id;
    return { component: 'WorkflowPageNavigationService', currentPageId, ...additional };
  }

  /** updated navigation item subject */
  private readonly navigationStateRetrievedSub: Subscription;

  /** updated navigation item subject */
  private readonly _pageNavigation$ = new Subject<NavigationEvent>();

  /** updated navigation item subject */
  private readonly _navItems$ = new Subject<WorkflowNavItem[]>();

  /** Page Submitted flag subject */
  private readonly _pageSubmitted$ = new BehaviorSubject<boolean>(false);

  /** Page Submitted flag subject */
  private readonly _hideValidation$ = new BehaviorSubject<boolean>(false);

  /** An observable of page Submitted flag  */
  public get pageSubmitted$(): Observable<boolean> {
    return this._pageSubmitted$.asObservable();
  }

  /** An observable of page Submitted flag  */
  public get hideValidation$(): Observable<boolean> {
    return this._hideValidation$.asObservable();
  }

  /** An observable of navigation route items  */
  public get navItems$(): Observable<WorkflowNavItem[]> {
    return this._navItems$.asObservable();
  }

  /** An observable of page navigation changes  */
  public get pageNavigation$(): Observable<NavigationEvent> {
    return this._pageNavigation$.asObservable();
  }

  /** Set the page submit flag */
  public set pageSubmitted(value: boolean) {
    this._pageSubmitted$.next(value);
  }

  /** Set the hide validation flag */
  public set hideValidation(value: boolean) {
    this._hideValidation$.next(value);
  }

  constructor(
    private readonly workflowService: WorkflowService,
    private readonly router: Router,
    private readonly pageStateService: WorkflowPageNavigationStateService
  ) {
    registerDevtoolsState(this, 'WorkflowPageNavigationService');
    this.pageStateService.setStoragePrefix(this.workflowService?.currentFlow?.id);
    this.navigationStateRetrievedSub = this.pageStateService.navigationStateRetrieved$.subscribe(this.refreshNavItems);
    this.subscribeRouteEvent();
    this.logger.debug('Workflow navigation service initialized', this.logContext({ flowId: this.workflowService?.currentFlow?.id }));
  }

  public reset(): void {
    this.pageSubmitted = false;
    this.hideValidation = false;
    this.logger.debug('Reset page submission state', this.logContext());
  }

  /** returns boolean value that user touched and completed
   * since state become completed on page load itself checking for
   * page touched to consider page completed
   */
  public isCompleted(pageId: string): boolean {
    const page = this.pageStateService.get(pageId);
    return page && page.isTouched && page.state === 'completed';
  }

  public navigateNextPage(fragment?: string) {
    // Get the review page
    let page = this.workflowService.findNextReviewPage();
    // if it has no fragment find the next page to redirect else goto review page
    if (!fragment?.length) {
      page = this.workflowService.findNextPage();
    } else {
      this.statefulService.wrap('ss:edit-review-page').value = false;
      // if it has fragment & check is there any incomplete page. If so then redirect to first incomplete page.
      const incompletePage = this.workflowService.findFirstIncompletePage();
      if (incompletePage) {
        const currentFlow = this.workflowService.currentFlow;
        const flowPath = currentFlow?.config?.path ?? currentFlow?.config?.id ?? currentFlow?.id ?? '';
        const pagePath = incompletePage.path ?? incompletePage.id;
        const redirectPath = flowPath ? `${flowPath}/${pagePath}`.replace(/\/+/g, '/') : pagePath;

        this.logger.warn(
          `Loopback detected; redirecting to first incomplete page ${redirectPath}`,
          this.logContext({ incompletePageId: incompletePage.id, redirectPath })
        );
        page = incompletePage;
        fragment = undefined;
      }
    }
    // Update visited state only for the current page.
    this.updateNavState(this.workflowService.currentPage, true);
    this.raiseNavigationEvent(NavigationEventType.NavigateNextPage);
    this.navigateByUrl(page, fragment);
    this.logger.debug('Navigated to next page', this.logContext({ targetPageId: page?.id, fragment }));
  }

  public getReviewPage() {
    return this.workflowService.findNextReviewPage();
  }

  /**
   * Get first Incomplete page
   * @returns Page
   */
  public getFirstInCompletePage() {
    return this.workflowService.findFirstIncompletePage();
  }

  public navigatePreviousPage(fragment?: string) {
    // Get the review page
    let page = this.workflowService.findNextReviewPage();
    // if it has no fragment find the previous page to redirect else goto review page
    if (!fragment?.length) {
      page = this.workflowService.findPreviousPage();
    }
    // Update visited state only for the current page.
    this.updateNavState(this.workflowService.currentPage, true);
    this.raiseNavigationEvent(NavigationEventType.NavigatePreviousPage);
    this.navigateByUrl(page, fragment, !fragment?.length);
    this.logger.debug('Navigated to previous page', this.logContext({ targetPageId: page?.id, fragment }));
  }

  /**
   * Go To Current or first page of the flowId
   */
  public goToCurrentPage() {
    if (this.workflowService.currentPage) {
      this.raiseNavigationEvent(NavigationEventType.NavigateToCurrentPage);
      this.navigateByUrl(this.workflowService.currentPage);
    }
  }

  public goToFirstPageOfFlow(flowId: string, pageIterationId?: number, fragment?: string) {
    const flow = this.workflowService.getFlowById(flowId);
    const pageId = flow.pages[0].id;
    this.goToPageOfFlow(flowId, pageId, pageIterationId, fragment);
  }

  public goToPageOfFlow(flowId: string, pageId: string, pageIterationId?: number, fragment?: string) {
    const flow = this.workflowService.getFlowById(flowId);
    if (flow) {
      const currentFlowPath = flow.config.path;
      this.workflowService.setCurrentFlowPath(currentFlowPath);
    }

    this.goToPage(pageId, pageIterationId, fragment);
  }

  public goToPage(pageId: string, pageIterationId?: number, fragment?: string) {
    const page = this.workflowService.findPage(pageId, pageIterationId);
    this.updateNavState(this.workflowService.currentPage, true);
    this.raiseNavigationEvent(NavigationEventType.NavigateToPage);
    this.navigateByUrl(page, fragment);
  }

  public navigateToPage(page: PageInstance, fragment?: string) {
    this.goToPage(page.id, page.pageIterationId, fragment);
  }

  /**
   * Update the state of page navigation based on page fields status
   */
  public updateNavStateWithPageProcessing(page: PageInstance): void {
    if (!page) {
      return;
    }

    this.updateNavState(page, false, true);
  }

  /**
   * Update the state of page navigation based on page fields status
   */
  public updateNavState(page: PageInstance, isPageVisited = false, isPageProcessing = false): void {
    const navPageState = this.pageStateService.get(page.id);
    if (isPageVisited) {
      navPageState.isVisited = true;
      if (navPageState.state === 'not-started') {
        navPageState.state = 'started';
      }
    }

    if (!navPageState.isTouched) navPageState.isTouched = page.helper.isPageTouched;
    if (navPageState.isVisited) {
      // TODO: Add more information to 'issues' so we know what the issues are
      navPageState.state = page.helper.isPageComplete ? 'completed' : 'issues';
    }

    if (isPageProcessing) {
      navPageState.state = 'processing';
    }
    this.pageStateService.update(page.id, navPageState);
    this._navItems$.next(this.getNavItems());
  }

  public unsubscribe(): void {
    if (this.navigationStateRetrievedSub) {
      this.navigationStateRetrievedSub.unsubscribe();
    }
  }

  public clearPageState(page: PageInstance): void {
    const navPageState = this.pageStateService.get(page.id);
    navPageState.isTouched = false;
    navPageState.isVisited = false;
    navPageState.state = 'not-started';
    this.pageStateService.update(page.id, navPageState);
  }

  /**
   * get navigation route items
   */
  private getNavItems(): WorkflowNavItem[] {
    const navItems = [];
    if (this.workflowService.currentFlow) {
      const pages = getFlowInfo(this.workflowService.currentFlow).pages || [];
      pages
        .filter((p) => !p.config.excludedInFlow)
        .forEach((page) => {
          // Handle Iterative page
          const pageState = this.pageStateService.get(page.id);
          navItems.push({
            completed: false,
            configTitle: page.customTitleOrDefault,
            disabled: !page.helper.allRequiredPagesComplete(pages) || page.disableNavigation,
            hidden: page.config.hiddenInNavigation || !page.visible,
            name: page.customTitleOrDefault,
            path: page.path,
            id: page.id,
            pageIterationId: page.pageIterationId,
            state: pageState ? pageState.state : 'not-started'
          } as WorkflowNavItem);
        });
    }
    return navItems;
  }

  /**
   * Refresh Navigation Items
   */
  private readonly refreshNavItems = () => {
    this._navItems$.next(this.getNavItems());
  };

  private raiseNavigationEvent(eventType: NavigationEventType) {
    this._pageNavigation$.next({ flow: this.workflowService.currentFlow, page: this.workflowService.currentPage, eventType });
  }

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  private navigateByUrl(page: PageInstance | string, fragment?: string, isPreviousPage = false) {
    if (page && page instanceof PageInstance) {
      if (page.isRedirectPage) {
        this.handelRedirectPage(page);
        return;
      }

      if (page.isActionPage) {
        this.handelActionPage(page, isPreviousPage);
        return;
      }

      this.routeNavigateByUrl(this.getFullPath(page), fragment, page.config.preserveQueryParam);
      return;
    }

    if (page) {
      this.routeNavigateByUrl(page as string, fragment);
    }
  }

  private routeNavigateByUrl(path: string, fragment?: string, preserveQueryParam = false) {
    const queryParamsHandling = preserveQueryParam ? 'preserve' : null;
    if (fragment) {
      this.router.navigate([path], { fragment, queryParamsHandling });
      return;
    }
    this.router.navigate([path], { queryParamsHandling });
  }

  private handelRedirectPage(page: PageInstance): void {
    // TODO: revisit / refactor
    // handle URLs with https: or https:
    const previousPage = this.getFullPath(page.previousPage);
    const nextPage = this.getFullPath(page.nextPage);

    const queryParamsHandling = page.config.preserveQueryParam ? 'merge' : null;
    this.router.navigate([page.redirectUrl], { queryParams: { previousPage, nextPage }, queryParamsHandling });
  }

  private handelActionPage(page: PageInstance, isPreviousPage = false): void {
    if (isPreviousPage) {
      this.navigateByUrl(page.previousPage);
      return;
    }
    new ActionPage(page, this.workflowService.currentFlow, this.statefulService, this.workflowLoader).run().then((res: boolean) => {
      this.updateNavState(page, true);
      if (page.nextPage && res) {
        this.raiseNavigationEvent(NavigationEventType.NavigateNextPage);
        this.navigateByUrl(page.nextPage);
      }
    });
  }

  private getFullPath(page: PageInstance): string {
    if (!page) {
      return '';
    }
    const flowPath = this.workflowService.getFlowPathWithPrefix(this.workflowService.currentFlow);
    return `${flowPath}/${page.path}`;
  }

  private subscribeRouteEvent() {
    this.router?.events
      ?.pipe(filter((event) => event instanceof NavigationError || event instanceof NavigationEnd || event instanceof NavigationStart))
      .subscribe((e: NavigationEnd | NavigationStart | NavigationError) => {
        const flow = this.workflowService.currentFlow;
        /** Since pageNavigation start/end function binded to router event below function triggered for any navigation outside workflow
         *  and causing 401 if it triggers any API with invalid sessionId
         *  To restrict function evaluation outside workflow navigation checking flow path available in URL
         */
        if (flow?.config && e.url.includes(flow.config.path)) {
          if (e instanceof NavigationStart) {
            this.evaluateFn(flow.config.pageNavigationStarted, e);
          }

          if (e instanceof NavigationEnd) {
            this.evaluateFn(flow.config.pageNavigationEnded, e);
            this.raiseNavigationEvent(NavigationEventType.NavigationEnd);
          }
        }
      });
  }

  private evaluateFn(logicFn: LogicFnRef, event: NavigationStart | NavigationEnd) {
    if (!logicFn) return;
    dependencies.logicService.evaluate(
      logicFn,
      {
        flow: this.workflowService.currentFlow,
        page: this.workflowService.currentPage,
        targetPage: this.workflowService.findPageByRouteUrl(event.url),
        routeEvent: event
      },
      'generic',
      null,
      this.workflowService.currentPage?.pageIterationId
    );
  }
}
